import { Controller } from "@hotwired/stimulus"
import TableFrameNavigator from "../../utils/table_frame_navigator"
import TableQueryState from "../../utils/table_query_state"

// Connects to data-controller="trips--index-filter"
// Builds filter URL from status, user_id, start_date, end_date and reloads trips_list turbo frame.
export default class extends Controller {
  static targets = ["status", "userId", "startDate", "endDate"]
  static outlets = ["trips--filter"]

  // Map Stimulus target names (camelCase) to URL param names
  static paramKeys = {
    status: "status",
    userId: "user_id",
    startDate: "start_date",
    endDate: "end_date"
  }

  initialize() {
    this.setFilterValues()
  }

  connect() {
    this.tableNavigator = new TableFrameNavigator({
      frameId: "trips_list",
      tableKey: "trips",
      onPopState: (queryState) => this.setFilterValues(queryState)
    })
  }

  disconnect() {
    this.tableNavigator?.disconnect()
  }

  setFilterValues(queryState = new TableQueryState(window.location.search)) {
    const urlSearchParams = queryState.toSearchParams()

    Object.entries(this.constructor.paramKeys).forEach(([targetName, paramKey]) => {
      const hasTargetProp = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProp]) {
        const target = this[`${targetName}Target`]
        if (target) target.value = urlSearchParams.get(paramKey) || ""
      }
    })
  }

  activeStatusChecker() {
    const tripsFilterController = this.tripsFilterOutlet

    if (tripsFilterController.hasActiveStatusStateTarget) {
      tripsFilterController.toggleActiveStatus(
        !!(this.hasStatusTarget && this.statusTarget?.value?.trim()),
        tripsFilterController.activeStatusStateTarget
      )
    }
    if (tripsFilterController.hasActiveStatusUserTarget && this.hasUserIdTarget) {
      tripsFilterController.toggleActiveStatus(
        !!this.userIdTarget?.value?.trim(),
        tripsFilterController.activeStatusUserTarget
      )
    }
    if (tripsFilterController.hasActiveStatusPeriodTarget) {
      const hasPeriod =
        (this.hasStartDateTarget && this.startDateTarget?.value) || (this.hasEndDateTarget && this.endDateTarget?.value)
      tripsFilterController.toggleActiveStatus(!!hasPeriod, tripsFilterController.activeStatusPeriodTarget)
    }
  }

  cancelFilters() {
    this.removeValuesFromFilters()
    this.#constructUrlAndSendRequest()
  }

  removeValuesFromFilters() {
    Object.keys(this.constructor.paramKeys).forEach((targetName) => {
      const camelName = targetName.charAt(0).toUpperCase() + targetName.slice(1)
      const hasTargetProp = `has${camelName}Target`
      if (this[hasTargetProp]) {
        const target = this[`${targetName}Target`]
        if (target) target.value = ""
      }
    })
  }

  cancelChipFilter(event) {
    const targetName = event.currentTarget.dataset.target
    const target = this[`${targetName}Target`]
    if (target) {
      target.value = ""
      this.#constructUrlAndSendRequest()
    }
  }

  applyFilters() {
    this.#constructUrlAndSendRequest()
  }

  #constructUrlAndSendRequest() {
    this.tableNavigator.navigate(this.#constructQueryState(), {
      history: "push",
      reason: "filter",
      preserveScroll: { x: true, y: false }
    })
  }

  #constructQueryState() {
    const changes = Object.fromEntries(Object.values(this.constructor.paramKeys).map((key) => [key, null]))

    Object.entries(this.constructor.paramKeys).forEach(([targetName, paramKey]) => {
      const hasTargetProp = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetProp]) {
        const target = this[`${targetName}Target`]
        const value = target?.value?.trim()
        if (value) changes[paramKey] = value
      }
    })
    return new TableQueryState(window.location.search).patch(changes)
  }
}
